'use client';

import { useDotEnv } from '@repo/dot-env/client';
import { type Coordinates, expandGoogleMapsLink, parseGoogleMapsInput } from '@repo/geolocation';
import { GoogleMapsProvider, LocationPickerMap } from '@repo/features/google-maps';
import { Input } from '@repo/ui/components';
import { TEST_IDS } from '@repo/sdk/constants';
import { useStore } from '@tanstack/react-form';
import { useState } from 'react';
import { withFieldGroup } from '../../lib/app-form-hook.tsx';
type LocationGeolocationFieldsProps = {
  disabled?: boolean;
  onCoordinatesSelected?: (coordinates: Coordinates) => void;
};
const locationGeolocationFieldsProps: LocationGeolocationFieldsProps = {};
export const LocationGeolocationFields = withFieldGroup({
  defaultValues: {
    hide_geolocation: true,
    lat: '',
    lng: ''
  },
  props: locationGeolocationFieldsProps,
  render: function Render({
    disabled,
    group,
    onCoordinatesSelected
  }) {
    const {
      GEOLOCATION_SERVICE_URL
    } = useDotEnv();
    const lat = useStore(group.store, store => store.values.lat);
    const lng = useStore(group.store, store => store.values.lng);
    const [mapsInput, setMapsInput] = useState('');
    const [mapsInputError, setMapsInputError] = useState('');
    const trimmedLat = lat.trim();
    const trimmedLng = lng.trim();
    const parsedPoint = trimmedLat && trimmedLng && Number.isFinite(Number(trimmedLat)) && Number.isFinite(Number(trimmedLng)) ? {
      lat: Number(trimmedLat),
      lng: Number(trimmedLng)
    } : null;
    const applyCoordinates = (coordinates: Coordinates, clearGoogleIdentity = false) => {
      group.setFieldValue('lat', String(coordinates.lat));
      group.setFieldValue('lng', String(coordinates.lng));
      if (clearGoogleIdentity) onCoordinatesSelected?.(coordinates);
      setMapsInputError('');
    };
    const applyMapsInput = async (value: string) => {
      const parsed = parseGoogleMapsInput(value);
      if (!parsed) {
        setMapsInputError('Enter supported Google Maps link or coordinates.');
        return;
      }
      if (parsed.kind === 'short-link') {
        const expandedUrl = await expandGoogleMapsLink(parsed.url, GEOLOCATION_SERVICE_URL);
        const expanded = expandedUrl ? parseGoogleMapsInput(expandedUrl) : null;
        if (!expanded || expanded.kind === 'short-link' || !expanded.coordinates) {
          setMapsInputError('Enter supported Google Maps link or coordinates.');
          return;
        }
        applyCoordinates(expanded.coordinates, true);
        return;
      }
      if (!parsed.coordinates) {
        setMapsInputError('Enter supported Google Maps link or coordinates.');
        return;
      }
      applyCoordinates(parsed.coordinates, true);
    };
    return <section className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <h3 className="text-2xl font-bold">Geolocation</h3>
          <p className="text-muted-foreground text-sm">
            Use the map to choose the location and pin it.
          </p>
        </div>
        <div className="py-2">
          <group.AppField name="hide_geolocation">
            {field => <field.checkbox data-testid={TEST_IDS.companyLocation.form.hideGeolocationCheckbox} description="If this option is selected, the pin will not be visible on the carrier’s Order Confirmation" disabled={disabled} label="Hide geolocation for carrier" />}
          </group.AppField>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="google-maps-input">
            Google Maps link or coordinates
          </label>
          <Input aria-describedby={mapsInputError ? 'google-maps-input-error' : undefined} disabled={disabled} id="google-maps-input" onBlur={() => {
          if (mapsInput.trim()) void applyMapsInput(mapsInput);
        }} onChange={event => setMapsInput(event.target.value)} onPaste={event => {
          const value = event.clipboardData.getData('text');
          setMapsInput(value);
          void applyMapsInput(value);
        }} placeholder="Paste a Google Maps link or coordinates" value={mapsInput} />
          {mapsInputError && <p className="text-destructive text-sm" id="google-maps-input-error" role="alert">
              {mapsInputError}
            </p>}
        </div>
        <div className="bg-muted h-36 overflow-hidden rounded-lg border">
          <GoogleMapsProvider>
            <LocationPickerMap className="size-full" disabled={disabled} onChange={point => {
            applyCoordinates(point);
          }} value={parsedPoint} />
          </GoogleMapsProvider>
        </div>
        <group.AppField name="lat">
          {field => <field.Input aria-label="Latitude" data-testid={TEST_IDS.companyLocation.form.latInput} disabled={disabled} label={'Latitude *'} placeholder="Latitude" />}
        </group.AppField>
        <group.AppField name="lng">
          {field => <field.Input aria-label="Longitude" data-testid={TEST_IDS.companyLocation.form.lngInput} disabled={disabled} label={'Longitude *'} placeholder="Longitude" />}
        </group.AppField>
      </section>;
  }
});